import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import {
  selectActiveLayers,
  selectLayerScaleVisibility,
} from '../../store/selectors/index.js';
import { allLayersCleared } from '../../store/slices/layersSlice.js';
import LayerListItem from './LayerListItem.js';

/**
 * The layers currently on the map, top of the stack first.
 *
 * The store keeps draw order bottom-first to match the SDK's own convention,
 * but people read a layer list the way they read the map: the thing drawn on
 * top belongs at the top. The reversal happens here, in the rendering layer,
 * rather than contorting the store to match a display preference.
 */
export default function LayerListPanel() {
  const dispatch = useAppDispatch();
  const layers = useAppSelector(selectActiveLayers);
  const scaleVisibility = useAppSelector(selectLayerScaleVisibility);

  if (layers.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <LayersOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No layers on the map yet.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Add one from Search or Explore.
        </Typography>
      </Box>
    );
  }

  const topFirst = layers.map((_, i) => layers[layers.length - 1 - i]!);

  return (
    <Box>
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {layers.length} {layers.length === 1 ? 'layer' : 'layers'}, drawn top to bottom
        </Typography>
        <Button size="small" onClick={() => dispatch(allLayersCleared())} sx={{ fontSize: '0.75rem' }}>
          Remove all
        </Button>
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {topFirst.map((layer, displayIndex) => (
          <LayerListItem
            key={layer.name}
            layer={layer}
            // Translate the display position back to the store's ordering.
            storeIndex={layers.length - 1 - displayIndex}
            isTop={displayIndex === 0}
            isBottom={displayIndex === layers.length - 1}
            total={layers.length}
            scaleVisibility={scaleVisibility.get(layer.name)}
          />
        ))}
      </Box>
    </Box>
  );
}
